import { useLocation, useNavigate } from "@tanstack/react-router";
import { BugIcon, RefreshCwIcon } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { AppLogo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { phaseSlugToTitle } from "@/data/match";
import { useOnline } from "@/hooks/useOnline";
import { usePWAUpdater } from "@/hooks/usePWAUpdater";
import { sentryEnabled as enableSentry, getSentryFeedback } from "@/sentry";

const NAVBAR_HEIGHT = 60;

type LayoutProps = {
  content: ReactNode;
};

export function Layout({ content }: LayoutProps): ReactNode {
  const navigate = useNavigate();
  const path = useLocation({ select: (state) => state.pathname });
  const { forceUpdate } = usePWAUpdater();
  const isOnline = useOnline();

  const feedback = getSentryFeedback();

  const matchPhaseTitle = useMemo(() => {
    const matchedPath = path.match(/\/scouting\/[^/]*\/collect\/(.*)/);

    let title = "Scouting";
    if (matchedPath && matchedPath.length === 2) {
      title = phaseSlugToTitle(matchedPath[1]);
    }

    return title;
  }, [path]);

  return (
    <div
      className="min-h-dvh flex flex-col"
      style={{ minHeight: `calc(100dvh - ${NAVBAR_HEIGHT}px)` }}
    >
      <header
        className="flex items-center px-2 border-b-2 bg-background select-none"
        style={{ height: NAVBAR_HEIGHT }}
      >
        <div className="flex-1 flex justify-start h-full">
          <AppLogo
            className="py-1 flex"
            classNames={{ text: "sm:block hidden" }}
            onClick={() => navigate({ to: "/" })}
          />
        </div>
        <div className="flex-1 flex justify-center">
          <h1 className="text-3xl lg:text-4xl leading-none text-center">
            {matchPhaseTitle}
          </h1>
        </div>
        <div className="flex-1 flex justify-end items-center gap-2">
          <Button
            className="hidden lg:block text-primary py-0 px-2"
            variant="ghost"
            onClick={() => navigate({ to: "/admin" })}
          >
            Scouting Admin
          </Button>
          {isOnline ? (
            <Button
              onClick={forceUpdate}
              className="px-2"
              title="Check for updates"
            >
              <RefreshCwIcon className="size-6!" />
            </Button>
          ) : null}
          {enableSentry && isOnline ? (
            <Button
              variant="destructive"
              onClick={async () => {
                const form = await feedback?.createForm();
                form?.appendToDom();
                form?.open();
              }}
              className="lg:px-4 px-2"
            >
              <span className="hidden lg:inline">Report Bug</span>
              <BugIcon className="inline lg:hidden size-6!" />
            </Button>
          ) : null}
        </div>
      </header>

      <main className="flex-1 flex flex-col max-w-5xl mx-auto w-full p-2 overflow-y-auto">
        {content}
      </main>
    </div>
  );
}
